import { ReactElement, useMemo } from 'react'
import { TableView } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import usePlanImageCardTool from '@editor/hooks/usePlanImageCardTool'
import usePlanTableTool from '@editor/hooks/usePlanTableTool'
import { ToggleButton } from '@ui/actions'
import { useAppStore } from '@stores'
import { useControlStore, useModelStore } from '@editorStores'
import { useResultsSelection } from '@editorHooks'
import {
  Toolbar as EditorToolbar,
  ToolbarGroup,
  StoreyControls,
  CommentToolSelect,
  PlanTableToolSelect,
  PlanImageCardToolSelect,
  useAddCommentTool,
} from '@editorComponents'
import useAnchorGridCloseGuard from '../AnchorDataGrid2/useAnchorGridCloseGuard'
import BlockpadDataButton from '../BlockpadDataButton'

const Toolbar = (): ReactElement => {
  const theme = useTheme()

  const { setShowComments } = useModelStore(state => state)
  const results2IntegratedAnchorView = useAppStore(state => state.results2IntegratedAnchorView)
  // With the integrated anchor view the anchors live in the Results2 "Anker" tab, so the
  // Zugverbindertabelle toggle disappears.
  const showAnchorTableButton = !results2IntegratedAnchorView
  const { activeMode: activeResultsMode, setResultsMode } = useResultsSelection()

  const { isActive: isAddCommentActive, toggle: toggleAddComment } = useAddCommentTool()
  const { isActive: isAddPlanTableActive, toggle: toggleAddPlanTable } = usePlanTableTool()
  const { isActive: isAddPlanImageCardActive, toggle: toggleAddPlanImageCard } =
    usePlanImageCardTool()

  const guardAnchorGridClose = useAnchorGridCloseGuard()

  const storey = useControlStore(state => state.floorplanState.storey)
  const setFloorplanStorey = useControlStore(state => state.setFloorplanStorey)

  const isBottomDrawerExpanded = useControlStore(state => state.isBottomDrawerExpanded)
  const anchorTableModeActive = activeResultsMode === 'tensile-connectors-table'

  const modeBarBottomOffset = useMemo(() => {
    if (!anchorTableModeActive) return undefined
    const drawerHeight = isBottomDrawerExpanded
      ? theme.distances.bottomDrawerHeightExpanded
      : theme.distances.bottomDrawerHeight
    return `calc(${drawerHeight} + ${theme.spacing(1)})`
  }, [anchorTableModeActive, isBottomDrawerExpanded, theme])

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 10,
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ pointerEvents: 'all' }}>
          <StoreyControls storey={storey} setStorey={setFloorplanStorey} />
        </Box>
      </Box>

      <EditorToolbar
        offsetBottom={modeBarBottomOffset}
        sx={{
          // allow drawing in the 3D scene through the bar’s empty hit area
          pointerEvents: 'none',
        }}
      >
        <ToolbarGroup>
          {showAnchorTableButton && (
            <ToggleButton
              hint="Zugverbindertabelle"
              tooltipPlacement="top"
              size="small"
              onClick={() => {
                const nextActive = activeResultsMode !== 'tensile-connectors-table'

                if (!nextActive && guardAnchorGridClose()) return

                setShowComments(false)
                setResultsMode(nextActive ? 'tensile-connectors-table' : null)
              }}
              selected={anchorTableModeActive}
              Icon={TableView}
              data-cy="open-anchor-table-button"
            />
          )}
          <BlockpadDataButton
            ripType="timber"
            iconOnly
            selected={activeResultsMode === 'blockpad'}
            onOpen={() => {
              setShowComments(false)
              setResultsMode('blockpad')
            }}
            onClose={activeResultsMode === 'blockpad' ? () => setResultsMode(null) : undefined}
          />
        </ToolbarGroup>

        <ToolbarGroup>
          <CommentToolSelect
            isAddCommentActive={isAddCommentActive}
            onToggleAddComment={toggleAddComment}
          />

          <PlanTableToolSelect
            isAddPlanTableActive={isAddPlanTableActive}
            onToggleAddPlanTable={toggleAddPlanTable}
          />

          <PlanImageCardToolSelect
            isAddPlanImageCardActive={isAddPlanImageCardActive}
            onToggleAddPlanImageCard={toggleAddPlanImageCard}
          />
        </ToolbarGroup>
      </EditorToolbar>
    </>
  )
}

export default Toolbar
